import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Grid,
  Popover,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Lock as LockIcon,
  Visibility as ViewIcon,
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import * as api from '../api/api';
import S3StatusAlert from './S3StatusAlert';

// Status options matching those in OutcomeInfoStep
const statusOptions = ["All", "Planning", "Ready for Review", "Ready for ITGC", "Approved", "Complete", "Paused"];
const allowedStatuses = ["Planning", "Ready for Review", "Ready for ITGC", "Approved", "Complete", "Paused"];

// Editable Status Chip Component
function EditableStatusChip({ 
  label, 
  value, 
  options, 
  onChange, 
  color = "default",
  variant = "filled",
  size = "small",
  isReadOnly = false
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [updating, setUpdating] = useState(false);
  
  const handleClick = (event) => {
    if (isReadOnly || updating) return;
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleSelect = async (newValue) => {
    if (isReadOnly || updating) return;
    
    try {
      setUpdating(true);
      await onChange(newValue);
      handleClose();
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setUpdating(false);
    }
  };
  
  const open = Boolean(anchorEl);
  
  return (
    <>
      <Chip
        label={updating ? 'Updating...' : value}
        color={color}
        variant={variant}
        size={size}
        onClick={handleClick}
        sx={{ 
          cursor: isReadOnly || updating ? 'default' : 'pointer',
          opacity: isReadOnly ? 0.7 : 1,
          '&:hover': (isReadOnly || updating) ? {} : { opacity: 0.8 }
        }}
        icon={isReadOnly ? <LockIcon fontSize="small" /> : (updating ? <CircularProgress size={12} /> : undefined)}
      />
      {!isReadOnly && (
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <List dense>
            {options.map((option) => (
              <ListItem
                key={option}
                button
                onClick={() => handleSelect(option)}
                selected={option === value}
                disabled={updating}
              >
                <ListItemText primary={option} />
              </ListItem>
            ))}
          </List>
        </Popover>
      )}
    </>
  );
}

function OutcomeOverview({ onEditOutcome, onNewOutcome, onViewOutcome, onBackToHome, isReadOnly = false, selectedDepartment = '' }) {
  const theme = useTheme();
  const [outcomes, setOutcomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [ownerFilter, setOwnerFilter] = useState('All');
  const [itOwnerFilter, setItOwnerFilter] = useState('All');
  const [projectCodeFilter, setProjectCodeFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState(selectedDepartment || 'All');
  const [availableDepartments, setAvailableDepartments] = useState([]);
    const [applications, setApplications] = useState([]);
  const [functionalOwners, setFunctionalOwners] = useState([]);
  const [itOwners, setItOwners] = useState([]);
  const [showS3Alert, setShowS3Alert] = useState(false);
  const [actionsMenu, setActionsMenu] = useState({ anchor: null, outcome: null });

        const fetchApplications = async () => {
    try {
      console.log('OutcomeOverview: Fetching applications...');
      const response = await api.getApplications();
      console.log('OutcomeOverview: Applications response:', response);
      
      let apps = [];
      if (Array.isArray(response)) {
        apps = response;
      } else if (response && Array.isArray(response.applications)) {
        apps = response.applications;
      } else {
        console.warn('OutcomeOverview: Unexpected applications response format:', response);
        apps = [];
      }
      
      console.log('OutcomeOverview: Processed applications:', apps);
      setApplications(apps);
      return apps;
    } catch (error) {
      console.error('OutcomeOverview: Error fetching applications:', error);
      setApplications([]);
      return [];
    }
  };

    const fetchFunctionalOwners = async () => {
    try {
      console.log('OutcomeOverview: Fetching functional owners...');
      const response = await api.getOwners();
      console.log('OutcomeOverview: Owners response:', response);
      
      const functionalOwnersList = response?.functional || [];
      console.log('OutcomeOverview: Processed functional owners:', functionalOwnersList);
      setFunctionalOwners(functionalOwnersList);
      setItOwners(response?.it || []);
      return functionalOwnersList;
    } catch (error) {
      console.error('OutcomeOverview: Error fetching functional owners:', error);
      setFunctionalOwners([]);
      return [];
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.getDepartments();
      let depts = [];
      if (Array.isArray(response)) {
        depts = response;
      } else if (response && Array.isArray(response.departments)) {
        depts = response.departments;
      }
      setAvailableDepartments(depts);
    } catch (error) {
      console.error('OutcomeOverview: Error fetching departments:', error);
      setAvailableDepartments([]);
    }
  };

      const handleS3StatusChange = useCallback((status) => {
    console.log('OutcomeOverview: S3 status update:', status);
    
    // Show alert only if S3 has issues (local data being used)
    const shouldShowAlert = !status.isHealthy;
    setShowS3Alert(shouldShowAlert);
    
    if (!status.isHealthy) {
      console.warn('OutcomeOverview: S3 storage unavailable, using local data');
    }
  }, []);

    const fetchOutcomes = async (deptOverride) => {
    try {
      setLoading(true);
      setError(null);
      console.log('OutcomeOverview: Fetching outcomes and applications...');

      const deptParam = deptOverride !== undefined ? deptOverride : selectedDepartment;
      console.log('OutcomeOverview: Using standard outcomes endpoint...');
      const startTime = performance.now();
      const outcomesResponse = await api.getOutcomes(deptParam || null);
      const endTime = performance.now();
      console.log(`OutcomeOverview: Standard endpoint completed in ${(endTime - startTime).toFixed(2)}ms`);
      
      // Fetch applications, owners, and departments in parallel
      const [appsData, ownersData] = await Promise.all([
        fetchApplications(),
        fetchFunctionalOwners(),
        fetchDepartments()
      ]);
      
                  // Handle response format - getOutcomes returns {outcomes: [...]}
      let outcomesList = outcomesResponse.outcomes || outcomesResponse || [];
      
      console.log('OutcomeOverview: Processed outcomes:', outcomesList.length, 'outcomes');
      if (outcomesList.length === 0) {
        console.warn('OutcomeOverview: No outcomes received from API');
      }
      
            // Filter out any outcomes that have the deleted flag (only if explicitly set to true)
      outcomesList = outcomesList.filter(outcome => outcome.deleted !== true);
      
      // Sort by last updated date (newest first), falling back to created_date
      outcomesList.sort((a, b) => {
        const dateA = new Date(a.last_updated || a.created_date || 0);
        const dateB = new Date(b.last_updated || b.created_date || 0);
        return dateB - dateA;
      });
      
                        console.log('OutcomeOverview: Final processed outcomes:', outcomesList.length, 'outcomes');
      console.log('OutcomeOverview: Sample outcome IDs:', outcomesList.slice(0, 5).map(o => o.id));
      console.log('OutcomeOverview: Sample outcome names:', outcomesList.slice(0, 5).map(o => o.name));
      setOutcomes(outcomesList);
        } catch (err) {
      console.error('OutcomeOverview: Error fetching outcomes:', err);
      
      // Check if this might be an S3-related error
      const errorMessage = err.response?.data?.detail || err.message;
      const isS3Error = errorMessage.toLowerCase().includes('s3') || 
                       errorMessage.toLowerCase().includes('aws') ||
                       errorMessage.toLowerCase().includes('storage') ||
                       err.response?.status === 500;
      
      if (isS3Error) {
        setError('Storage connection issue detected. Please check the storage status below.');
        setShowS3Alert(true); // Force show S3 alert on storage errors
      } else {
        setError('Failed to load outcomes: ' + errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOutcomeStatusChange = async (outcomeId, newStatus) => {
    if (isReadOnly) return;
    
    try {
      console.log('OutcomeOverview: Updating status for outcome:', outcomeId, 'to:', newStatus);
      
      // Find the outcome in the list
      const outcome = outcomes.find(o => o.id === outcomeId);
      if (!outcome) {
        console.error('OutcomeOverview: Outcome not found:', outcomeId);
        throw new Error('Outcome not found');
      }

      // Update the outcome status
      const updatedOutcome = { 
        ...outcome, 
        status: newStatus, 
        last_updated: new Date().toISOString() 
      };
      console.log('OutcomeOverview: Sending update:', updatedOutcome);
      
      const response = await api.updateOutcome(outcomeId, updatedOutcome);
      console.log('OutcomeOverview: Update response:', response);

      // Update local state immediately for better UX
      setOutcomes(prev => 
        prev.map(o => o.id === outcomeId ? { ...o, status: newStatus } : o)
      );

      // Trigger refresh event for other components
      window.dispatchEvent(new CustomEvent('outcomeStatusUpdated', { 
        detail: { outcomeId, newStatus } 
      }));
      
      console.log('OutcomeOverview: Status update completed successfully');
    } catch (error) {
      console.error('OutcomeOverview: Error updating outcome status:', error);
      
      // Revert local state on error
      await fetchOutcomes();
      
      setError('Failed to update outcome status: ' + (error.response?.data?.detail || error.message));
      throw error; // Re-throw to let the chip component handle the error state
    }
  };

    const handleDeleteOutcome = async (outcomeId) => {
    if (isReadOnly) return;
    
    if (window.confirm('Are you sure you want to delete this outcome?')) {
      try {
        setLoading(true);
        console.log(`OutcomeOverview: Attempting to delete outcome with ID: ${outcomeId}`);
        
        await api.deleteOutcome(outcomeId);
        console.log(`OutcomeOverview: Successfully deleted outcome with ID: ${outcomeId}`);
        
        // Refresh the outcomes list after deletion
        await fetchOutcomes();
        
        // Show success message
        setError(null);
      } catch (err) {
        console.error('OutcomeOverview: Error deleting outcome:', err);
        setError('Failed to delete outcome: ' + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDuplicateOutcome = async (outcome) => {
    if (isReadOnly) return;
    
    if (window.confirm(`Are you sure you want to duplicate "${outcome.name || 'Untitled'}"?`)) {
      try {
        setLoading(true);
        console.log('OutcomeOverview: Duplicating outcome:', outcome);
        
        // Fetch the full outcome data including staffing and cost estimation
        const [fullOutcome, staffingData, costEstimation] = await Promise.allSettled([
          api.getOutcome(outcome.id),
          api.getStaffingData(outcome.id).catch(() => null),
          api.getCostEstimation(outcome.id).catch(() => null)
        ]);
        
        const sourceOutcome = fullOutcome.status === 'fulfilled' ? fullOutcome.value : outcome;
        const sourceStaffing = staffingData.status === 'fulfilled' ? staffingData.value : null;
        const sourceCostEst = costEstimation.status === 'fulfilled' ? costEstimation.value : null;
        
        console.log('OutcomeOverview: Source data fetched:', {
          outcome: sourceOutcome,
          hasStaffing: !!sourceStaffing,
          hasCostEst: !!sourceCostEst
        });
        
        // Create the duplicated outcome data
        const duplicatedOutcome = {
          ...sourceOutcome,
          // Remove ID and system fields
          id: undefined,
          created_date: undefined,
          last_updated: undefined,
          
          // Update name with copy suffix
          name: `Copy of ${sourceOutcome.name || 'Untitled'}`,
          
          // Reset status to Planning
          status: 'Planning',
          
          // Keep most other fields including:
          // - applications (array of app IDs)
          // - functional_owner
          // - tags
          // - milestones
          // - description, etc.
          
          // Remove plan assignment (as requested)
          plan_id: null,
          assigned_to_plan: null
        };
        
        console.log('OutcomeOverview: Creating duplicated outcome:', duplicatedOutcome);
        
        // Create the new outcome
        const createdOutcome = await api.createOutcome(duplicatedOutcome);
        console.log('OutcomeOverview: Outcome duplicated successfully:', createdOutcome);
        
        const newOutcomeId = createdOutcome.id || createdOutcome.outcome?.id;
        
        if (!newOutcomeId) {
          throw new Error('Failed to get ID of created outcome');
        }
        
        // Duplicate staffing data if it exists
        if (sourceStaffing && sourceStaffing.milestones) {
          try {
            console.log('OutcomeOverview: Duplicating staffing data...');
            
            // Create a clean copy of staffing data
            const duplicatedStaffing = {
              milestones: sourceStaffing.milestones.map(milestone => ({
                ...milestone,
                // Keep all milestone data including roles and assignments
                // The backend will handle generating new IDs for roles
              }))
            };
            
            await api.saveStaffingData(newOutcomeId, duplicatedStaffing);
            console.log('OutcomeOverview: Staffing data duplicated successfully');
          } catch (staffingError) {
            console.warn('OutcomeOverview: Failed to duplicate staffing data:', staffingError);
            // Don't fail the entire duplication for staffing errors
          }
        }
        
        // Duplicate cost estimation data if it exists
        if (sourceCostEst && (sourceCostEst.roles || sourceCostEst.cost_breakdown)) {
          try {
            console.log('OutcomeOverview: Duplicating cost estimation data...');
            
            // Create a clean copy of cost estimation data
            const duplicatedCostEst = {
              ...sourceCostEst,
              // Remove any ID fields that might exist
              id: undefined,
              outcome_id: newOutcomeId
            };
            
            await api.saveCostEstimation(newOutcomeId, duplicatedCostEst);
            console.log('OutcomeOverview: Cost estimation data duplicated successfully');
          } catch (costError) {
            console.warn('OutcomeOverview: Failed to duplicate cost estimation data:', costError);
            // Don't fail the entire duplication for cost estimation errors
          }
        }
        
        // Refresh the outcomes list
        await fetchOutcomes();
        
        // Show success message
        setError(null);
        
        // Trigger events for other components
        window.dispatchEvent(new CustomEvent('outcomeCreated', { 
          detail: { outcomeId: newOutcomeId, source: 'duplication' } 
        }));
        
        console.log('OutcomeOverview: Duplication completed successfully');
        
      } catch (err) {
        console.error('OutcomeOverview: Error duplicating outcome:', err);
        setError('Failed to duplicate outcome: ' + (err.response?.data?.detail || err.message));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditOutcome = (outcome) => {
    if (isReadOnly) return;
    console.log('OutcomeOverview: Editing outcome:', outcome);
    onEditOutcome(outcome);
  };

  const handleViewOutcome = (outcome) => {
    if (onViewOutcome) {
      console.log('OutcomeOverview: Viewing outcome:', outcome);
      onViewOutcome(outcome);
    }
  };

  const handleNewOutcome = () => {
    if (isReadOnly) return;
    console.log('OutcomeOverview: Creating new outcome');
    onNewOutcome();
  };

  const handleRowClick = (outcome, event) => {
    // Don't trigger row click if clicking on action buttons
    if (event.target.closest('button') || event.target.closest('[role="button"]')) {
      return;
    }
    
    if (isReadOnly) {
      handleViewOutcome(outcome);
    } else {
      handleEditOutcome(outcome);
    }
  };

      useEffect(() => {
    let isMounted = true;
    
    const loadInitialData = async () => {
      if (isMounted) {
        await fetchOutcomes();
      }
    };
    
    loadInitialData();
    
    // Listen for outcome updates from other components
    const handleOutcomeUpdate = (event) => {
      console.log('OutcomeOverview: Received outcome update event, refreshing...', event.detail);
      // Add a small delay to ensure backend has processed the update
      if (isMounted) {
        setTimeout(() => {
          if (isMounted) {
            fetchOutcomes();
          }
        }, 500);
      }
    };
    
        // Listen for application updates to refresh application names
    const handleApplicationUpdate = (event) => {
      console.log('OutcomeOverview: Received application update event, refreshing applications...', event.detail);
      fetchApplications();
    };
    
    // Listen for owner updates to refresh owner names
    const handleOwnerUpdate = (event) => {
      console.log('OutcomeOverview: Received owner update event, refreshing owners...', event.detail);
      fetchFunctionalOwners();
    };
    
        window.addEventListener('outcomeUpdated', handleOutcomeUpdate);
    window.addEventListener('outcomeStatusUpdated', handleOutcomeUpdate);
    window.addEventListener('outcomeCreated', handleOutcomeUpdate);
    window.addEventListener('applicationCreated', handleApplicationUpdate);
    window.addEventListener('applicationUpdated', handleApplicationUpdate);
    window.addEventListener('applicationDeleted', handleApplicationUpdate);
    window.addEventListener('ownerCreated', handleOwnerUpdate);
    window.addEventListener('ownerUpdated', handleOwnerUpdate);
    window.addEventListener('ownerDeleted', handleOwnerUpdate);
    
        return () => {
      isMounted = false;
      window.removeEventListener('outcomeUpdated', handleOutcomeUpdate);
      window.removeEventListener('outcomeStatusUpdated', handleOutcomeUpdate);
      window.removeEventListener('outcomeCreated', handleOutcomeUpdate);
      window.removeEventListener('applicationCreated', handleApplicationUpdate);
      window.removeEventListener('applicationUpdated', handleApplicationUpdate);
      window.removeEventListener('applicationDeleted', handleApplicationUpdate);
      window.removeEventListener('ownerCreated', handleOwnerUpdate);
      window.removeEventListener('ownerUpdated', handleOwnerUpdate);
      window.removeEventListener('ownerDeleted', handleOwnerUpdate);
    };
    // eslint-disable-next-line
  }, []);

  // Re-fetch when global department filter changes; sync local staffing-tab filter
  useEffect(() => {
    setDepartmentFilter(selectedDepartment || 'All');
    fetchOutcomes(selectedDepartment);
    // eslint-disable-next-line
  }, [selectedDepartment]);

    const getStatusColor = (status) => {
    switch (status) {
      case 'Planning': return 'warning';
      case 'Ready for Review': return 'info';
      case 'Ready for ITGC': return 'secondary';
      case 'Approved': return 'success';
      case 'Complete': return 'success';
      case 'Paused': return 'default';
      default: return 'default';
    }
  };

  const getFunctionalOwnerName = (ownerId) => {
    if (!ownerId) return 'Unassigned';
    const owner = functionalOwners.find(o => o.id === ownerId);
    return owner ? owner.name : 'Unknown Owner';
  };

    const filteredOutcomes = outcomes.filter(o => {
    // Filter by name search
    const nameMatch = o && (o.name || '').toLowerCase().includes(search.toLowerCase());
    
    // Filter by status
    const statusMatch = statusFilter === 'All' || o.status === statusFilter;
    
    // Filter by functional owner
    const ownerMatch = ownerFilter === 'All' ||
                      (ownerFilter === 'Unassigned' && !o.functional_owner) ||
                      o.functional_owner === ownerFilter;

    // Filter by IT owner
    const itOwnerMatch = itOwnerFilter === 'All' ||
                        (itOwnerFilter === 'Unassigned' && !o.it_owner) ||
                        o.it_owner === itOwnerFilter;

    // Filter by project code
    const projectCodeMatch = !projectCodeFilter ||
      (o.project_code || '').toLowerCase().includes(projectCodeFilter.toLowerCase());

    // Filter by department
    const departmentMatch = departmentFilter === 'All' ||
      o.department === departmentFilter ||
      (o.department_budgets || []).some(db => db.department_name === departmentFilter);

    return nameMatch && statusMatch && ownerMatch && itOwnerMatch && projectCodeMatch && departmentMatch;
  });

  // Debug logging for filtered outcomes
  console.log('OutcomeOverview: Filtering debug:', {
    totalOutcomes: outcomes.length,
    filteredCount: filteredOutcomes.length,
    searchTerm: search,
    statusFilter,
    ownerFilter,
    sampleFiltered: filteredOutcomes.slice(0, 3).map(o => ({ id: o.id, name: o.name || 'EMPTY', status: o.status }))
  });

  // Button style with better contrast for dark mode
  const buttonStyle = {
    backgroundColor: theme.palette.mode === 'dark' 
      ? theme.palette.primary.main 
      : undefined,
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? alpha(theme.palette.primary.main, 0.8) 
        : undefined,
    }
  };

    // Helper function to render application tags
  const renderApplicationTags = (outcomeApplications) => {
    if (!outcomeApplications || !Array.isArray(outcomeApplications) || outcomeApplications.length === 0) {
      return <Typography variant="body2" color="text.secondary">None</Typography>;
    }
    
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {outcomeApplications.map((appId, index) => {
          // Find the application by ID
          const app = applications.find(a => a.id === appId);
          const displayName = app ? app.name : appId; // Fallback to ID if app not found
          
          return (
            <Chip 
              key={appId || index} 
              label={displayName} 
              size="small" 
              color="primary" 
              variant="outlined"
              sx={{ margin: '2px' }}
              title={app ? `Status: ${app.status}` : 'Application not found'}
            />
          );
        })}
      </Box>
    );
  };

  return (
    <Box>
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {onBackToHome && (
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={onBackToHome}
                >
                  Home
                </Button>
              )}
              <Box>
                <Typography variant="h4" component="h1">
                  Outcome Management
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                  {isReadOnly ? 'View your project outcomes and their progress' : 'Manage your project outcomes and track their progress'}
                  {isReadOnly && ' (Read-Only Mode)'}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {!isReadOnly ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNewOutcome}
                  size="large"
                  sx={buttonStyle}
                >
                  New Outcome
                </Button>
              ) : (
                <Tooltip title="Switch to Edit Mode to create new outcomes">
                  <span>
                    <Button
                      variant="contained"
                      startIcon={<LockIcon />}
                      disabled
                      size="large"
                    >
                      New Outcome
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>
                </CardContent>
      </Card>
      
      {/* S3 Status Alert */}
      <S3StatusAlert 
        onStatusChange={handleS3StatusChange}
        showOnSuccess={false}
        autoCheck={true}
      />
      
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search outcomes..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 200, flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            displayEmpty
            renderValue={(selected) => (!selected || selected === 'All') ? 'All Statuses' : selected}
          >
            {statusOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={ownerFilter}
            onChange={e => setOwnerFilter(e.target.value)}
            displayEmpty
            renderValue={(selected) => {
              if (!selected || selected === 'All') return 'All Owners';
              if (selected === 'Unassigned') return 'Unassigned';
              const owner = functionalOwners.find(o => o.id === selected);
              return owner ? owner.name : 'Unknown';
            }}
          >
            <MenuItem value="All">All Owners</MenuItem>
            <MenuItem value="Unassigned">Unassigned</MenuItem>
            {functionalOwners.map(owner => (
              <MenuItem key={owner.id} value={owner.id}>{owner.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={itOwnerFilter}
            onChange={e => setItOwnerFilter(e.target.value)}
            displayEmpty
            renderValue={(selected) => {
              if (!selected || selected === 'All') return 'All IT Owners';
              if (selected === 'Unassigned') return 'Unassigned';
              const owner = itOwners.find(o => o.id === selected);
              return owner ? owner.name : 'Unknown';
            }}
          >
            <MenuItem value="All">All IT Owners</MenuItem>
            <MenuItem value="Unassigned">Unassigned</MenuItem>
            {itOwners.map(owner => (
              <MenuItem key={owner.id} value={owner.id}>{owner.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          placeholder="Project code..."
          value={projectCodeFilter}
          onChange={e => setProjectCodeFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 120 }}
        />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={departmentFilter}
            onChange={e => setDepartmentFilter(e.target.value)}
            displayEmpty
            renderValue={(selected) => (!selected || selected === 'All') ? 'All Departments' : selected}
          >
            <MenuItem value="All">All Departments</MenuItem>
            {availableDepartments.map(dept => (
              <MenuItem key={dept.id} value={dept.name}>{dept.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress size={24} sx={{ mr: 2 }} />
          <Typography color="text.secondary">Loading outcomes...</Typography>
        </Box>
      )}
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {!loading && !error && (
        filteredOutcomes.length === 0 ? (
          <Card elevation={2}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No outcomes found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {search || statusFilter !== 'All' || projectCodeFilter ?
                  'Try adjusting your search or filters' :
                  'Get started by creating your first outcome'}
              </Typography>
              {!isReadOnly ? (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNewOutcome}
                  sx={buttonStyle}
                >
                  Create New Outcome
                </Button>
              ) : (
                <Tooltip title="Switch to Edit Mode to create outcomes">
                  <span>
                    <Button
                      variant="contained"
                      startIcon={<LockIcon />}
                      disabled
                    >
                      Create New Outcome
                    </Button>
                  </span>
                </Tooltip>
              )}
            </CardContent>
          </Card>
        ) : (
          <TableContainer component={Paper} elevation={2}>
            <Table>
                            <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Functional Owner</TableCell>
                  <TableCell>Applications</TableCell>
                  <TableCell>Project Code</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOutcomes.map((outcome, index) => (
                  <TableRow 
                    key={outcome.id || index}
                    onClick={(event) => handleRowClick(outcome, event)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="subtitle2">
                        {outcome.name || 'Untitled'}
                      </Typography>
                    </TableCell>
                                                                                <TableCell onClick={(e) => e && e.stopPropagation && e.stopPropagation()}>
                      <EditableStatusChip
                        value={outcome.status || 'Planning'}
                        options={allowedStatuses}
                        onChange={(newStatus) => handleOutcomeStatusChange(outcome.id, newStatus)}
                        color={getStatusColor(outcome.status)}
                        size="small"
                        isReadOnly={isReadOnly}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={outcome.functional_owner ? 'text.primary' : 'text.secondary'}>
                        {getFunctionalOwnerName(outcome.functional_owner)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {renderApplicationTags(outcome.applications)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {outcome.project_code || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {outcome.last_updated
                          ? new Date(outcome.last_updated).toLocaleDateString()
                          : outcome.created_date
                            ? new Date(outcome.created_date).toLocaleDateString()
                            : 'Unknown'}
                      </Typography>
                    </TableCell>
                                                                                <TableCell onClick={(e) => e && e.stopPropagation && e.stopPropagation()}>
                      {!isReadOnly ? (
                        <>
                          <IconButton
                            size="small"
                            onClick={(e) => setActionsMenu({ anchor: e.currentTarget, outcome })}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                          <Menu
                            anchorEl={actionsMenu.outcome?.id === outcome.id ? actionsMenu.anchor : null}
                            open={actionsMenu.outcome?.id === outcome.id}
                            onClose={() => setActionsMenu({ anchor: null, outcome: null })}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MenuItem onClick={() => { setActionsMenu({ anchor: null, outcome: null }); handleEditOutcome(outcome); }}>
                              <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
                            </MenuItem>
                            <MenuItem onClick={() => { setActionsMenu({ anchor: null, outcome: null }); handleDuplicateOutcome(outcome); }}>
                              <ContentCopyIcon fontSize="small" sx={{ mr: 1 }} /> Duplicate
                            </MenuItem>
                            <MenuItem onClick={() => { setActionsMenu({ anchor: null, outcome: null }); handleDeleteOutcome(outcome.id); }} sx={{ color: 'error.main' }}>
                              <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
                            </MenuItem>
                          </Menu>
                        </>
                      ) : (
                        <Tooltip title="View outcome details">
                          <IconButton
                            onClick={() => handleViewOutcome(outcome)}
                            color="primary"
                            size="small"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}
    </Box>
  );
}

export default OutcomeOverview;